import { Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { StrongText } from "@/components/strong-text";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { type RoadmapItem, useDeleteRoadmapItem } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { seesRoadmapHours } from "@/lib/roles";
import { cn } from "@/lib/utils";

import { BlockersCard } from "./details-blockers";
import { CommentsCard } from "./details-comments";
import { DeveloperCard } from "./details-developer";
import { DetailsHeader } from "./details-header";
import { type DetailsContext, Failure, useFocusBack } from "./details-parts";
import { PeopleCard } from "./details-people";
import { PlanningCard } from "./details-planning";
import { DetailsStage } from "./details-stage";
import { StepButtons } from "./step-buttons";

interface ItemDetailsDialogProps {
  /** The item on show; null when there is none, or it left the roadmap (deleted). */
  item: RoadmapItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overview: DetailsContext;
  /** The month Inplannen offers first ("2026-11"): the month on show, never a past one. */
  planMonth: string;
  /**
   * A past month is on show: the details only show the item, but for a
   * developer's own part (`ItemDetailsPanel`).
   */
  pastMonth?: boolean;
  /** Opens another item's details, such as one a blockage of this item names. */
  onOpenItem: (id: string) => void;
}

/**
 * An item's details in a centred dialog, workflow first (`ItemDetailsPanel`).
 * When the item leaves the roadmap (deleted here or by someone else) the
 * dialog closes, and keeps the item it last had while it goes. The dialog
 * itself takes the focus when it opens, and gives it back to what opened it.
 */
export function ItemDetailsDialog({
  item: latest,
  open,
  onOpenChange,
  overview,
  planMonth,
  pastMonth = false,
  onOpenItem,
}: ItemDetailsDialogProps) {
  // The last item on show, kept while the dialog closes after its item went.
  const [last, setLast] = useState(latest);
  if (latest !== null && latest !== last) {
    setLast(latest);
  }
  const item = latest ?? last;
  // The dialog has no trigger of its own: remember what opened it, and go back there on close.
  const opener = useRef<HTMLElement | null>(null);
  const content = useRef<HTMLDivElement>(null);
  return (
    <Dialog open={open && latest !== null} onOpenChange={onOpenChange}>
      <DialogContent
        ref={content}
        className="max-h-[90dvh] overflow-y-auto focus:outline-none sm:max-w-2xl"
        onOpenAutoFocus={(event) => {
          opener.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
          // The dialog itself takes the focus: no pencil's tooltip pops up, and Enter moves nothing.
          event.preventDefault();
          content.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          if (opener.current?.isConnected) {
            event.preventDefault();
            opener.current.focus();
          }
        }}
      >
        {item && (
          // Afresh per item, so an edit or a step never carries over to another item.
          <ItemDetailsPanel
            key={item.id}
            item={item}
            overview={overview}
            planMonth={planMonth}
            pastMonth={pastMonth}
            onClose={() => onOpenChange(false)}
            onOpenItem={onOpenItem}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The cards that edit in place, each with its own pencil. */
type CardKey = "content" | "planning" | "blockers" | "developer";

interface ItemDetailsPanelProps {
  item: RoadmapItem;
  overview: DetailsContext;
  /** The month Inplannen offers first ("2026-11"). */
  planMonth: string;
  /**
   * A past month is on show, where nothing changes: no step, no follow button,
   * no pencil and a note that says so. A developer keeps their own part, so a
   * done item's actual hours and an item's effort and month still change.
   */
  pastMonth?: boolean;
  /** Closes the details, after the item was deleted. */
  onClose: () => void;
  onOpenItem: (id: string) => void;
}

/**
 * The body of an item's details, workflow first. On top the priority, the
 * title and the summary (the dialog's title and description, so it renders
 * inside a `<Dialog>`). Then where the item stands, as four stages, with the
 * one next step the server allows (`DetailsStage`). Under it one card per
 * concern, each with its own pencil: Planning (without the effort for an
 * admin, who sees no hours), Blokkades (none on a done item), Personen and,
 * for a developer, Ontwikkelaar. Then the comments (`CommentsCard`), and
 * Verwijderen at the very bottom, asked first.
 */
export function ItemDetailsPanel({
  item,
  overview,
  planMonth,
  pastMonth = false,
  onClose,
  onOpenItem,
}: ItemDetailsPanelProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<ReadonlySet<CardKey>>(new Set());
  const [deleting, setDeleting] = useState(false);
  // Annuleren under the question hands the focus back to Verwijderen.
  const deleteButton = useFocusBack(deleting);
  const developer = Boolean(item.developer);

  const setCard = (card: CardKey, on: boolean) =>
    setEditing((current) => {
      const next = new Set(current);
      if (on) {
        next.add(card);
      } else {
        next.delete(card);
      }
      return next;
    });

  return (
    <div className="space-y-4">
      <DetailsHeader
        item={item}
        pastMonth={pastMonth}
        editing={editing.has("content")}
        onEditing={(on) => setCard("content", on)}
      />
      <DetailsStage
        item={item}
        planMonth={planMonth}
        pastMonth={pastMonth}
        onChooseEffort={() => setCard("planning", true)}
      />
      <PlanningCard
        item={item}
        pastMonth={pastMonth}
        showHours={seesRoadmapHours(overview.viewer.role)}
        editing={editing.has("planning")}
        onEditing={(on) => setCard("planning", on)}
      />
      <BlockersCard
        item={item}
        overview={overview}
        pastMonth={pastMonth}
        editing={editing.has("blockers")}
        onEditing={(on) => setCard("blockers", on)}
        onOpenItem={onOpenItem}
      />
      <div className={cn("grid gap-4", developer && "sm:grid-cols-2")}>
        <PeopleCard item={item} overview={overview} pastMonth={pastMonth} />
        {developer && (
          <DeveloperCard
            item={item}
            editing={editing.has("developer")}
            onEditing={(on) => setCard("developer", on)}
          />
        )}
      </div>
      <CommentsCard item={item} overview={overview} pastMonth={pastMonth} />
      {/* Asked already: the question stays when a refusal shows the item may no longer go
          (someone started it meanwhile), so its failure says why. */}
      {(item.can.delete || deleting) && !pastMonth && (
        <div className="border-t pt-3">
          {deleting ? (
            <DeleteStep item={item} onBack={() => setDeleting(false)} onDeleted={onClose} />
          ) : (
            <div className="flex justify-end">
              <Button
                ref={deleteButton}
                variant="ghost"
                size="sm"
                onClick={() => setDeleting(true)}
                className="text-destructive hover:text-destructive"
                data-action="delete"
              >
                <Trash2 aria-hidden="true" className="size-4" />
                {t("roadmap.details.delete")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface DeleteStepProps {
  item: RoadmapItem;
  /** Back without a change: Annuleren. */
  onBack: () => void;
  /** After the item is gone: the details close. */
  onDeleted: () => void;
}

/**
 * Verwijderen, asked first at the bottom of the details. Annuleren takes the
 * focus, from the Verwijderen it replaces: the keyboard stays at the bottom,
 * on the safe choice.
 */
export function DeleteStep({ item, onBack, onDeleted }: DeleteStepProps) {
  const { t } = useTranslation();
  const remove = useDeleteRoadmapItem();
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  const submit = async () => {
    if (remove.isPending) {
      return;
    }
    setFailure(null);
    try {
      await remove.mutateAsync({ itemId: item.id });
      onDeleted();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <div className="space-y-3" data-section="delete">
      <p className="text-sm">
        <StrongText i18nKey="roadmap.details.deleteBody" values={{ item: item.title }} />
      </p>
      {failure && <Failure>{apiErrorMessage(failure.error, "deleteRoadmapItem")}</Failure>}
      <StepButtons
        onBack={onBack}
        onConfirm={() => void submit()}
        confirm={t("roadmap.details.delete")}
        pending={remove.isPending}
        destructive
        autoFocusBack
      />
    </div>
  );
}
